import type { AgentPermissions } from "../../../../../src/multi-agent/types";

export interface ExecutionLimits {
  maxIterations?: number;
  maxTokens?: number;
  maxCost?: number;
  maxDurationSeconds?: number;
  maxConcurrentSubagents?: number;
}

export type ArchitectureType = "sequential" | "supervisor" | "parallel" | "custom";
export type CollaborationMode = ArchitectureType;

export interface ProfileAgent {
  agentId: string;
  order?: number;
  modelOverride?: string;
  additionalTools?: string[];
  enabled?: boolean;
}

export interface ExecutionProfileRecord {
  id: string;
  name: string;
  description: string;
  architectureType: ArchitectureType;
  agents: ProfileAgent[];
  modelProviderId?: string;
  defaultModelId?: string;
  limits: ExecutionLimits;
  createdAt: number;
  updatedAt: number;
  lastUsedAt?: number;
  runCount: number;
}

export type ExecutionProfile = ExecutionProfileRecord;

export interface ExecutionProfileDraft {
  id?: string;
  name: string;
  description: string;
  architectureType: ArchitectureType;
  agentIds: string[];
  modelProviderId: string;
  defaultModelId: string;
  limits: ExecutionLimits;
  agentOverrides: Record<string, AgentOverrideDraft>;
}

export interface AgentOverrideDraft {
  modelId?: string;
  fallbackModelIds?: string[];
  permissions?: AgentPermissions;
  executionLimits?: ExecutionLimits;
  role?: string;
}

export interface ModelProviderInfo {
  id: string;
  name: string;
  status: "connected" | "disconnected" | "unconfigured";
  defaultModelId: string;
  availableModels: ModelInfo[];
}

export interface ModelInfo {
  id: string;
  label: string;
  provider: string;
  supportsStreaming: boolean;
  supportsVision: boolean;
  contextWindow: number;
  costPer1kInput: number;
  costPer1kOutput: number;
}

export interface ToolGroup {
  id: string;
  label: string;
  tools: ToolInfo[];
}

export interface ToolInfo {
  id: string;
  label: string;
  description: string;
  dangerLevel: "safe" | "moderate" | "dangerous";
  group: string;
}

export interface ToolPermissionEntry {
  tool: string;
  action: "allow" | "deny" | "inherit";
}

export interface ValidationError {
  field: string;
  message: string;
  code?: string;
  nodeId?: string;
  edgeId?: string;
  suggestion?: string;
}

export interface MultiAgentSettingsState {
  profiles: ExecutionProfileRecord[];
  profilesLoading: boolean;
  profilesError: string | null;
  draft: ExecutionProfileDraft | null;
  draftDirty: boolean;
  draftErrors: ValidationError[];
  selectedProfileId: string | null;
  activeTab: MultiAgentSettingsTab;
  activeProfileTab: MultiAgentSettingsProfileTab;
  saving: boolean;
  saveError: string | null;
  deleting: boolean;
  deleteError: string | null;
  selectedNodeId: string | null;
}

export type MultiAgentSettingsTab = "profiles" | "model-providers" | "templates" | "policies";

export type MultiAgentSettingsProfileTab =
  | "basic"
  | "agents"
  | "model"
  | "tools"
  | "limits"
  | "builder"
  | "preview"
  | "nesting";

export interface ArchitectureTypeMetadata {
  id: ArchitectureType;
  value: ArchitectureType;
  name: string;
  label: string;
  description: string;
  icon: string;
  minAgents: number;
  maxAgents: number;
  supportsNesting: boolean;
}

export const ARCHITECTURE_TYPES: ArchitectureTypeMetadata[] = [
  {
    id: "sequential",
    value: "sequential",
    name: "Sequential Pipeline",
    label: "Sequential",
    description: "Agents execute one after another in a pipeline",
    icon: "→",
    minAgents: 1,
    maxAgents: 10,
    supportsNesting: false,
  },
  {
    id: "supervisor",
    value: "supervisor",
    name: "Supervisor",
    label: "Supervisor",
    description: "One agent delegates tasks to multiple subordinates",
    icon: "⤵",
    minAgents: 2,
    maxAgents: 50,
    supportsNesting: true,
  },
  {
    id: "parallel",
    value: "parallel",
    name: "Parallel Execution",
    label: "Parallel",
    description: "Multiple agents work simultaneously",
    icon: "⤴",
    minAgents: 2,
    maxAgents: 20,
    supportsNesting: false,
  },
  {
    id: "custom",
    value: "custom",
    name: "Custom Graph",
    label: "Custom",
    description: "Build a free-form graph with arbitrary connections",
    icon: "⬡",
    minAgents: 1,
    maxAgents: 100,
    supportsNesting: true,
  },
];

export const DEFAULT_EXECUTION_LIMITS: ExecutionLimits = {
  maxIterations: 100,
  maxTokens: 100_000,
  maxCost: 10,
  maxDurationSeconds: 3600,
  maxConcurrentSubagents: 4,
};

export type AgentRole =
  | "architect"
  | "lead"
  | "developer"
  | "reviewer"
  | "security"
  | "tester"
  | "deployer"
  | "researcher"
  | "analyst"
  | "orchestrator";

export interface ProfileAgentDraft {
  id: string;
  name: string;
  role: AgentRole;
  model: ModelConfigDraft;
  tools: ToolConfigDraft;
  limits: LimitsDraft;
}

export interface ModelConfigDraft {
  provider: string;
  model: string;
  fallbacks: FallbackModelDraft[];
  parameters: ModelParametersDraft;
}

export interface FallbackModelDraft {
  provider: string;
  model: string;
  condition: "unavailable" | "error" | "cost_threshold";
}

export interface ModelParametersDraft {
  temperature: number;
  maxTokens: number;
  topP: number;
  streaming: boolean;
}

export interface ToolConfigDraft {
  profile: string;
  allowedTools: string[];
  deniedTools: string[];
  allowedGroups: string[];
  deniedGroups: string[];
}

export interface LimitsDraft {
  maxIterations: number;
  maxTokens: number;
  maxCost: number;
  maxDuration: number;
  maxSubagents: number;
}

export interface ProfileSettingsDraft {
  sharedContext: {
    enabled: boolean;
    propagation: "all" | "none" | "selected" | "inherited";
    sharedFields: string[];
  };
  communication: {
    messageBus: "in-memory" | "queue" | "stream";
    enableHeartbeat: boolean;
  };
  errorHandling: {
    strategy: "stop" | "continue" | "retry" | "fallback";
    maxRetries: number;
    fallbackAgentId?: string;
  };
}

export interface LegacyExecutionProfileDraft {
  id: string;
  name: string;
  description: string;
  collaborationMode: CollaborationMode;
  agents: ProfileAgentDraft[];
  settings: ProfileSettingsDraft;
  validationErrors: ValidationError[];
  isDirty: boolean;
}

export interface AgentRoleMetadata {
  id: AgentRole;
  name: string;
  description: string;
  icon: string;
  defaultCapabilities: string[];
}

export const AGENT_ROLES: AgentRoleMetadata[] = [
  {
    id: "architect",
    name: "Architect",
    description: "System design and planning",
    icon: "🏗️",
    defaultCapabilities: ["codeAnalysis", "apiDesign"],
  },
  {
    id: "lead",
    name: "Lead",
    description: "Technical coordination",
    icon: "👷",
    defaultCapabilities: ["codeAnalysis", "codeReview"],
  },
  {
    id: "developer",
    name: "Developer",
    description: "Code implementation",
    icon: "💻",
    defaultCapabilities: ["codeExecution", "refactoring"],
  },
  {
    id: "reviewer",
    name: "Reviewer",
    description: "Code review",
    icon: "🔍",
    defaultCapabilities: ["codeReview", "codeAnalysis"],
  },
  {
    id: "security",
    name: "Security",
    description: "Security analysis",
    icon: "🔒",
    defaultCapabilities: ["securityScan", "codeAnalysis"],
  },
  {
    id: "tester",
    name: "Tester",
    description: "Testing and QA",
    icon: "🧪",
    defaultCapabilities: ["testFramework", "bugFix"],
  },
  {
    id: "deployer",
    name: "Deployer",
    description: "Deployment",
    icon: "🚀",
    defaultCapabilities: ["deployment", "monitoring"],
  },
  {
    id: "researcher",
    name: "Researcher",
    description: "Research",
    icon: "🔬",
    defaultCapabilities: ["research", "dataAnalysis"],
  },
  {
    id: "analyst",
    name: "Analyst",
    description: "Analysis",
    icon: "📊",
    defaultCapabilities: ["dataAnalysis", "documentation"],
  },
  {
    id: "orchestrator",
    name: "Orchestrator",
    description: "Coordination",
    icon: "🎭",
    defaultCapabilities: ["codeAnalysis", "monitoring"],
  },
];

export interface ToolProfileMetadata {
  id: string;
  name: string;
  description: string;
  tools: string[];
}

export const TOOL_PROFILES: ToolProfileMetadata[] = [
  {
    id: "minimal",
    name: "Minimal",
    description: "Read-only access",
    tools: ["read", "find", "glob"],
  },
  {
    id: "coding",
    name: "Coding",
    description: "File and code operations",
    tools: ["read", "write", "edit", "exec", "find", "glob"],
  },
  {
    id: "messaging",
    name: "Messaging",
    description: "Communication",
    tools: ["message", "email_send", "email_receive"],
  },
  { id: "full", name: "Full Access", description: "All tools", tools: ["*"] },
];

export interface ModelProviderMetadata {
  id: string;
  name: string;
  models: ModelMetadata[];
}

export interface ModelMetadata {
  id: string;
  name: string;
  provider: string;
  capabilities: string[];
  contextWindow: number;
  inputCostPer1k: number;
  outputCostPer1k: number;
}

export const MODEL_CATALOG: ModelProviderMetadata[] = [
  {
    id: "openai",
    name: "OpenAI",
    models: [
      {
        id: "gpt-4o",
        name: "GPT-4o",
        provider: "openai",
        capabilities: ["vision", "function-calling", "streaming"],
        contextWindow: 128000,
        inputCostPer1k: 0.005,
        outputCostPer1k: 0.015,
      },
      {
        id: "gpt-4o-mini",
        name: "GPT-4o Mini",
        provider: "openai",
        capabilities: ["function-calling", "streaming"],
        contextWindow: 128000,
        inputCostPer1k: 0.00015,
        outputCostPer1k: 0.0006,
      },
      {
        id: "gpt-4-turbo",
        name: "GPT-4 Turbo",
        provider: "openai",
        capabilities: ["vision", "function-calling", "streaming"],
        contextWindow: 128000,
        inputCostPer1k: 0.01,
        outputCostPer1k: 0.03,
      },
    ],
  },
  {
    id: "anthropic",
    name: "Anthropic",
    models: [
      {
        id: "claude-3-5-sonnet",
        name: "Claude 3.5 Sonnet",
        provider: "anthropic",
        capabilities: ["vision", "function-calling", "streaming"],
        contextWindow: 200000,
        inputCostPer1k: 0.003,
        outputCostPer1k: 0.015,
      },
      {
        id: "claude-3-5-haiku",
        name: "Claude 3.5 Haiku",
        provider: "anthropic",
        capabilities: ["function-calling", "streaming"],
        contextWindow: 200000,
        inputCostPer1k: 0.0008,
        outputCostPer1k: 0.004,
      },
    ],
  },
  {
    id: "google",
    name: "Google",
    models: [
      {
        id: "gemini-1.5-pro",
        name: "Gemini 1.5 Pro",
        provider: "google",
        capabilities: ["vision", "function-calling", "streaming"],
        contextWindow: 2000000,
        inputCostPer1k: 0.00125,
        outputCostPer1k: 0.005,
      },
      {
        id: "gemini-1.5-flash",
        name: "Gemini 1.5 Flash",
        provider: "google",
        capabilities: ["function-calling", "streaming"],
        contextWindow: 1000000,
        inputCostPer1k: 0.000075,
        outputCostPer1k: 0.0003,
      },
    ],
  },
];

export function createEmptyDraft(): LegacyExecutionProfileDraft {
  return {
    id: "",
    name: "New Profile",
    description: "",
    collaborationMode: "sequential",
    agents: [],
    settings: {
      sharedContext: {
        enabled: true,
        propagation: "all",
        sharedFields: ["task", "context", "artifacts"],
      },
      communication: { messageBus: "in-memory", enableHeartbeat: true },
      errorHandling: { strategy: "stop", maxRetries: 3 },
    },
    validationErrors: [],
    isDirty: false,
  };
}

export function createEmptyAgentDraft(): ProfileAgentDraft {
  return {
    id: crypto.randomUUID(),
    name: "",
    role: "developer",
    model: {
      provider: "openai",
      model: "gpt-4o-mini",
      fallbacks: [],
      parameters: { temperature: 0.7, maxTokens: 4096, topP: 1, streaming: true },
    },
    tools: {
      profile: "coding",
      allowedTools: [],
      deniedTools: [],
      allowedGroups: [],
      deniedGroups: [],
    },
    limits: {
      maxIterations: 100,
      maxTokens: 100000,
      maxCost: 10,
      maxDuration: 3600000,
      maxSubagents: 5,
    },
  };
}
